<template>
  <ConfirmDialog ref="confirm" />

  <v-container>
    <v-form v-model="valid" ref="form">
      <card>
        <DataTableVirtual
          :loading="loading"
          :headers="headers"
          :items="ndsList"
          :item-value="itemKey"
          v-show="canShowTable"
          class="no-table-border"
          disable-sort
          fixed-header
        >
          <template v-slot:item.name="{ item }">
            <TextArea
              v-if="item.isNew"
              v-model.trim="item.name"
              :rules="[].concat(validation.required(true))"
              placeholder="Введите наименование"
              persistent-hint
              :hint="'ㅤ'"
              :rows="1"
              maxRows="3"
              maxlength="20"
              class="grid-fields"
            />
            <span
              v-else
              class="grid-fields"
            >
              {{ item.name }}
            </span>
          </template>
          <template v-slot:item.rate="{ item }">
            <CountInput
              v-if="item.isNew"
              placeholder="Число"
              :precision="0"
              v-model="item.rate"
              persistent-hint
              :hint="'ㅤ'"
              :rules="[].concat(validation.number(0, 99, true, true, true))"
              :min="0"
              :max="99"
              class="grid-fields"
            />
            <span
              v-else
              class="grid-fields"
            >
              {{ formatRate(item.rate) }}
            </span>
          </template>
          <template v-slot:item.actions="{ item }">
            <div :class="{ 'actions': item.isNew }">
              <v-icon v-if="!item.isActive" icon="mdi-close" @click="remove(item)" />
            </div>
          </template>
        </DataTableVirtual>
        <div class="d-flex flex-row align-center">
          <v-btn
            prepend-icon="mdi-plus"
            color="primary"
            density="compact"
            variant="elevated"
            class="ml-4 rounded"
            size="small"
            @click="add"
          >{{ $t('Добавить') }}</v-btn>
          <v-btn
            v-if="canShowTable"
            color="primary"
            density="compact"
            variant="outlined"
            class="ml-4 rounded"
            size="small"
            @click="removeAll"
          >{{ $t('Удалить все') }}</v-btn>
          <v-btn
            color="primary"
            density="compact"
            variant="outlined"
            class="ml-4 rounded"
            size="small"
            @click="save"
            :disabled="!valid"
          >{{ $t('Сохранить') }}</v-btn>
        </div>
      </card>
    </v-form>
  </v-container>
</template>

<script>

import api from '@/api';
import Card from '@intec/vue-components/form/Card.vue';
import TextArea from '@intec/vue-components/form/TextArea.vue';
import { toast } from '@/lib/toast';
import ConfirmDialog from "@intec/vue-components/form/ConfirmDialog.vue";
import validation from '@/components/validation';
import { formatRate } from '@/filters';
import CountInput from '@/components/form/CountInput.vue';
import DataTableVirtual from "@intec/vue-components/table/DataTableVirtual.vue"

export default {
  name: 'Nds',
  components: {
    CountInput,
    TextArea,
    ConfirmDialog,
    Card,
    DataTableVirtual
  },
  data() {
    return {
      loading: false,
      valid: true,
      ndsList: [],
      tempIdCounter: 0, // Счётчик для временных id
      headers: [
        {
          title: 'Наименование',
          key: 'name',
          headerProps: {
            'data-required': 'true'
          },
          width: '31%'
        },
        {
          title: 'Числовое значение',
          key: 'rate',
          headerProps: {
            'data-required': 'true'
          },
          width: '30%'
        },
        {
          key: 'actions',
          width: '3%',
          align: 'end'
        }
      ],
      validation,
      formatRate,
    }
  },
  mounted() {
    this.fetchNds();
  },
  computed: {
    canShowTable() {
      return this.ndsList.length > 0;
    },
  },
  methods: {
    itemKey(item) {
      return item.id ?? item._tempId;
    },
    async fetchNds() {
      this.loading = true;
      try {
        const { entries } = await api.get('/vocab/nds', { excludeWithoutNds: true });
        this.ndsList = entries;
        this.tempIdCounter = 0;
      } catch (e) {
        toast.error(e.message);
      } finally {
        this.loading = false;
      }
    },
    add() {
      this.tempIdCounter++;
      this.ndsList.push({
        _tempId: 'temp_' + this.tempIdCounter,
        name: null,
        rate: null,
        isActive: false,
        isNew: true,
      });
    },
    async save() {
      const isValid = await this.$refs.form.validate();
      if (!isValid) {
        toast.error('Заполните все обязательные поля');
        return;
      }

      try {
        this.loading = true;

        const formData = new FormData();
        formData.append('data', JSON.stringify({ ndsList: this.ndsList }))
        await api.post('/admin/administration/nds/save', formData);

        toast.info('Изменения сохранены!');
      } catch (e) {
        toast.error(e.message);
      } finally {
        this.loading = false;
        await this.fetchNds();
      }
    },
    remove(item) {
      this.ndsList = this.ndsList.filter(nds => this.itemKey(nds) !== this.itemKey(item));
    },
    async removeAll() {
      let approve = await this.$refs.confirm.open(
        'Удаление ставок НДС',
        'Вы действительно хотите удалить все неиспользуемые ставки НДС?',
      );
      if (approve) {
        this.ndsList = this.ndsList.filter(item => item.isActive);
      }
    },
  }
}
</script>

<style scoped>
:deep(.v-data-table) {
  th {
    background-color: rgb(var(--grey-lighten-color)) !important;
    .v-data-table-header__content {
      font-size: 16px;
      color: #000 !important;
    }
  }
  [data-required] {
    span::after {
      content: ' *';
      color: rgba(var(--error-color));
    }
  }
  .v-table__wrapper > table > tbody > tr > td {
    padding: 0;
  }
}

.grid-fields {
  margin: 10px 5px;
}

.no-table-border {
  border: none;
  border-radius: 0;
}

.actions {
  margin-bottom: 18px;
}
</style>
